import { useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import {
  carregarImagem,
  montarCartela,
  type CartelaCaixa,
  type CartelaDados,
} from '@/lib/cartela';

interface Props {
  rifaId: number;
}

const CAIXA_QR_PADRAO: CartelaCaixa = { x: 71.26, y: 8.4, largura: 21.62, altura: 34.11 };
const CAIXA_NOME_PADRAO: CartelaCaixa = { x: 70.96, y: 79.17, largura: 25.81, altura: 6.91 };

const EXTENSOES_OK = ['image/jpeg', 'image/png', 'image/webp'];
const TAMANHO_MAX = 8 * 1024 * 1024;

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function fmtPct(v: number): string {
  return v.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
}

// Converte qualquer imagem em PNG (a arte é guardada como .png), limitando o lado maior.
function arquivoParaPng(arquivo: File, maxLado = 2000): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(arquivo);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      const maior = Math.max(width, height);
      if (maior > maxLado) {
        const fator = maxLado / maior;
        width = Math.round(width * fator);
        height = Math.round(height * fator);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Não foi possível processar a arte.'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (b) =>
          b
            ? resolve(b)
            : reject(new Error('Não foi possível processar a arte.')),
        'image/png',
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Não foi possível ler a arte.'));
    };
    img.src = url;
  });
}

export default function CartelaEditor({ rifaId }: Props) {
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [erroTecnico, setErroTecnico] = useState<string | null>(null);
  const [recado, setRecado] = useState<string | null>(null);

  const [arteUrl, setArteUrl] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const [nome, setNome] = useState('VENDEDOR');
  const [caixaQR, setCaixaQR] = useState<CartelaCaixa>(CAIXA_QR_PADRAO);
  const [caixaNome, setCaixaNome] = useState<CartelaCaixa>(CAIXA_NOME_PADRAO);
  const [caixaQRSalva, setCaixaQRSalva] = useState<CartelaCaixa>(CAIXA_QR_PADRAO);
  const [caixaNomeSalva, setCaixaNomeSalva] = useState<CartelaCaixa>(CAIXA_NOME_PADRAO);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);

  const [enviando, setEnviando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  const qrRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const blobUrlRef = useRef<string | null>(null);

  function limparBlob() {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }

  // Carrega os dados da cartela (com um vendedor real, se houver) + a arte.
  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setErro(null);
    (async () => {
      try {
        const { data, error } = await supabase.rpc('cartela_do_vendedor', {
          p_rifa: rifaId,
          p_vendedor: null,
        });
        if (error) throw error;
        let c = (data as CartelaDados | null) ?? null;

        // Sem vendedor associado à conta (ou sem arte): pega arte + caixas direto da rifa.
        if (!c || !c.arte_url) {
          const { data: r } = await supabase
            .from('rifas')
            .select('arte_url, arte_caixa_qr, arte_caixa_nome')
            .eq('id', rifaId)
            .maybeSingle();
          const rifa = r as {
            arte_url: string | null;
            arte_caixa_qr: CartelaCaixa | null;
            arte_caixa_nome: CartelaCaixa | null;
          } | null;
          if (rifa?.arte_url) {
            c = {
              vendedor_id: 0,
              nome: 'VENDEDOR',
              token: null,
              faixa: null,
              link: null,
              arte_url: rifa.arte_url,
              caixa_qr: rifa.arte_caixa_qr ?? CAIXA_QR_PADRAO,
              caixa_nome: rifa.arte_caixa_nome ?? CAIXA_NOME_PADRAO,
              evento: null,
              valor_cota_reais: null,
              arquivo: 'cartela.jpg',
              mensagem: '',
            };
          }
        }

        if (!ativo) return;
        if (!c || !c.arte_url) {
          setArteUrl(null);
          setCarregando(false);
          return;
        }
        setArteUrl(c.arte_url);
        setLink(c.link ?? null);
        setNome(c.nome || 'VENDEDOR');
        setCaixaQR(c.caixa_qr);
        setCaixaNome(c.caixa_nome);
        setCaixaQRSalva(c.caixa_qr);
        setCaixaNomeSalva(c.caixa_nome);
        setCarregando(false);
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar a cartela.'));
          setCarregando(false);
        }
      }
    })();
    return () => {
      ativo = false;
    };
  }, [rifaId, tentativa]);

  // Monta a prévia (arte + QR + nome) usando as caixas SALVAS.
  useEffect(() => {
    if (!arteUrl) {
      limparBlob();
      setPreviewSrc(null);
      return;
    }
    if (!link) {
      limparBlob();
      setPreviewSrc(arteUrl);
      return;
    }
    let ativo = true;
    (async () => {
      try {
        await new Promise<void>((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
        );
        const qr = qrRef.current;
        if (!qr) throw new Error('QR não gerado.');
        const img = await carregarImagem(arteUrl!);
        if (!ativo) return;
        const blob = await montarCartela(img, qr, {
          vendedor_id: 0,
          nome,
          token: null,
          faixa: null,
          link,
          arte_url: arteUrl,
          caixa_qr: caixaQRSalva,
          caixa_nome: caixaNomeSalva,
          evento: null,
          valor_cota_reais: null,
          arquivo: 'cartela.jpg',
          mensagem: '',
        });
        if (!ativo) return;
        const url = URL.createObjectURL(blob);
        limparBlob();
        blobUrlRef.current = url;
        setPreviewSrc(url);
      } catch {
        if (ativo) {
          limparBlob();
          setPreviewSrc(arteUrl);
        }
      }
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arteUrl, link, nome, caixaQRSalva, caixaNomeSalva]);

  useEffect(() => () => limparBlob(), []);

  async function copiarErro() {
    if (!erroTecnico) return;
    try {
      await navigator.clipboard.writeText(erroTecnico);
    } catch {
      /* ignora */
    }
  }

  async function aoEscolherArte(arquivo: File | null) {
    if (!arquivo) return;
    setErro(null);
    setErroTecnico(null);
    setRecado(null);
    if (!EXTENSOES_OK.includes(arquivo.type)) {
      setErro('Use uma imagem jpg, png ou webp.');
      return;
    }
    if (arquivo.size > TAMANHO_MAX) {
      setErro('Esta imagem tem mais de 8 MB. Escolha uma menor.');
      return;
    }
    setEnviando(true);
    try {
      const png = await arquivoParaPng(arquivo, 2000);
      const caminho = `rifas/${rifaId}/arte-${Date.now()}.png`;
      const { error: errUpload } = await supabase.storage
        .from('capas')
        .upload(caminho, png, { contentType: 'image/png' });
      if (errUpload) {
        setErroTecnico(mensagemAmigavel(errUpload, 'Erro ao enviar a arte.'));
        return;
      }
      const { data } = supabase.storage.from('capas').getPublicUrl(caminho);
      const url = data.publicUrl;
      const { error } = await supabase.rpc('definir_arte_da_rifa', {
        p_rifa: rifaId,
        p_url: url,
        p_caixa_qr: caixaQRSalva,
        p_caixa_nome: caixaNomeSalva,
      });
      if (error) throw error;
      setArteUrl(url);
      setRecado('Arte trocada. Ajuste as caixas se precisar.');
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setEnviando(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function salvarCaixas() {
    if (!arteUrl) return;
    setSalvando(true);
    setErro(null);
    setRecado(null);
    try {
      const { error } = await supabase.rpc('definir_arte_da_rifa', {
        p_rifa: rifaId,
        p_url: arteUrl,
        p_caixa_qr: caixaQR,
        p_caixa_nome: caixaNome,
      });
      if (error) throw error;
      setCaixaQRSalva(caixaQR);
      setCaixaNomeSalva(caixaNome);
      setRecado('Caixas salvas.');
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não foi possível salvar as caixas.'));
    } finally {
      setSalvando(false);
    }
  }

  function voltarPadrao() {
    setCaixaQR(caixaQRSalva);
    setCaixaNome(caixaNomeSalva);
  }

  function iniciarArrasto(e: React.PointerEvent, tipo: 'qr' | 'nome') {
    e.preventDefault();
    e.stopPropagation();
    const container = containerRef.current;
    if (!container) return;
    const caixa = tipo === 'qr' ? caixaQR : caixaNome;
    const rect = container.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startCaixaX = caixa.x;
    const startCaixaY = caixa.y;

    const mover = (ev: PointerEvent) => {
      const dxPct = ((ev.clientX - startX) / rect.width) * 100;
      const dyPct = ((ev.clientY - startY) / rect.height) * 100;
      const nx = clamp(startCaixaX + dxPct, 0, 100 - caixa.largura);
      const ny = clamp(startCaixaY + dyPct, 0, 100 - caixa.altura);
      if (tipo === 'qr') setCaixaQR((c) => ({ ...c, x: nx, y: ny }));
      else setCaixaNome((c) => ({ ...c, x: nx, y: ny }));
    };
    const soltar = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
  }

  if (carregando) {
    return (
      <section className="rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
        <div className="h-6 w-44 animate-pulse rounded bg-secondary-100" />
        <div className="mt-3 h-64 animate-pulse rounded-2xl bg-secondary-100" />
      </section>
    );
  }

  if (erro && !arteUrl) {
    return (
      <section className="rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
        <h2 className="px-1 text-[17px] font-bold text-foreground-950">Cartela do vendedor</h2>
        <p className="mt-2 px-1 text-[14px] text-foreground-800">{erro}</p>
        <button
          type="button"
          onClick={() => setTentativa((v) => v + 1)}
          className="mt-3 cursor-pointer whitespace-nowrap rounded-full border border-secondary-300 bg-white px-4 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-50"
        >
          Tentar de novo
        </button>
      </section>
    );
  }

  const semArte = !arteUrl;
  const mudouCaixa =
    caixaQR.x !== caixaQRSalva.x ||
    caixaQR.y !== caixaQRSalva.y ||
    caixaNome.x !== caixaNomeSalva.x ||
    caixaNome.y !== caixaNomeSalva.y;

  return (
    <section className="rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <div className="flex flex-wrap items-center justify-between gap-3 px-1">
        <h2 className="text-[17px] font-bold text-foreground-950">Cartela do vendedor</h2>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={enviando}
          className="flex min-h-[40px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-secondary-300 bg-white px-4 text-[14px] font-semibold text-foreground-800 transition-colors hover:bg-background-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-image-edit-line" />
          </span>
          {semArte ? 'Enviar arte' : 'Trocar a arte'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          className="sr-only"
          onChange={(e) => aoEscolherArte(e.target.files?.[0] ?? null)}
        />
      </div>

      <p className="mt-1 px-1 text-[13px] text-foreground-500">
        Cada vendedor baixa a cartela dele com o próprio QR e o próprio nome.
      </p>

      {semArte ? (
        <div className="mt-3 flex flex-col items-center rounded-2xl border border-dashed border-secondary-300 bg-secondary-50 px-6 py-10 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-secondary-400">
            <i className="ri-image-line text-2xl" />
          </span>
          <p className="mt-3 text-[14px] text-foreground-600">
            A organização ainda não subiu a arte da cartela.
          </p>
        </div>
      ) : (
        <>
          <div
            ref={containerRef}
            className="relative mt-3 overflow-hidden rounded-2xl border border-secondary-200"
            style={{ touchAction: 'none' }}
          >
            {link && (
              <div className="hidden" aria-hidden="true">
                <QRCodeCanvas
                  ref={qrRef}
                  value={link}
                  size={512}
                  level="M"
                  fgColor="#182878"
                  bgColor="#ffffff"
                />
              </div>
            )}
            {previewSrc && (
              <img
                src={previewSrc}
                alt="Prévia da cartela do vendedor"
                className="block w-full select-none"
                draggable={false}
              />
            )}

            {/* Caixa do QR (arrastável) */}
            <div
              className="absolute flex cursor-move items-start justify-center border-2 border-dashed border-primary-500 bg-primary-500/10"
              style={{
                left: `${caixaQR.x}%`,
                top: `${caixaQR.y}%`,
                width: `${caixaQR.largura}%`,
                height: `${caixaQR.altura}%`,
                touchAction: 'none',
              }}
              onPointerDown={(e) => iniciarArrasto(e, 'qr')}
            >
              <span className="mt-0.5 rounded bg-primary-500 px-1.5 py-0.5 text-[11px] font-bold text-white">
                QR
              </span>
            </div>

            {/* Caixa do nome (arrastável) */}
            <div
              className="absolute flex cursor-move items-start justify-center border-2 border-dashed border-accent-500 bg-accent-500/10"
              style={{
                left: `${caixaNome.x}%`,
                top: `${caixaNome.y}%`,
                width: `${caixaNome.largura}%`,
                height: `${caixaNome.altura}%`,
                touchAction: 'none',
              }}
              onPointerDown={(e) => iniciarArrasto(e, 'nome')}
            >
              <span className="mt-0.5 rounded bg-accent-500 px-1.5 py-0.5 text-[11px] font-bold text-white">
                Nome
              </span>
            </div>
          </div>

          {/* Valores em % + ações */}
          <div className="mt-3 rounded-2xl bg-background-50 p-3">
            <div className="grid grid-cols-2 gap-3 text-[13px] text-foreground-600">
              <div>
                <span className="font-semibold text-primary-700">QR</span>
                <p className="tabular-nums">
                  x {fmtPct(caixaQR.x)} · y {fmtPct(caixaQR.y)} · {fmtPct(caixaQR.largura)} ×{' '}
                  {fmtPct(caixaQR.altura)}
                </p>
              </div>
              <div>
                <span className="font-semibold text-accent-700">Nome</span>
                <p className="tabular-nums">
                  x {fmtPct(caixaNome.x)} · y {fmtPct(caixaNome.y)} · {fmtPct(caixaNome.largura)} ×{' '}
                  {fmtPct(caixaNome.altura)}
                </p>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={salvarCaixas}
                disabled={salvando || !mudouCaixa}
                className="flex min-h-[44px] flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl bg-primary-500 px-4 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {salvando ? (
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-loader-4-line animate-spin" />
                  </span>
                ) : (
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-check-line" />
                  </span>
                )}
                Salvar
              </button>
              <button
                type="button"
                onClick={voltarPadrao}
                disabled={!mudouCaixa}
                className="flex min-h-[44px] cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-4 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-go-back-line" />
                </span>
                Voltar ao padrão
              </button>
            </div>
          </div>
        </>
      )}

      {enviando && (
        <p className="mt-2 flex items-center gap-2 px-1 text-sm text-secondary-500">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-loader-4-line animate-spin" />
          </span>
          Enviando arte…
        </p>
      )}
      {recado && <p className="mt-2 px-1 text-sm text-accent-700">{recado}</p>}
      {erro && (
        <p className="mt-2 px-1 text-sm text-danger-600" role="alert">
          {erro}
        </p>
      )}
      {erroTecnico && (
        <div className="mt-3 rounded-xl border border-danger-200 bg-danger-50 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-danger-700">Não foi possível salvar a arte.</p>
              <p className="mt-1 break-words text-sm text-danger-700">{erroTecnico}</p>
            </div>
            <button
              type="button"
              onClick={copiarErro}
              className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-lg border border-danger-300 bg-white px-2 py-1 text-xs font-medium text-danger-700 transition-colors hover:bg-danger-100"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-file-copy-line" />
              </span>
              Copiar erro
            </button>
          </div>
        </div>
      )}
    </section>
  );
}